import React, {useState, useEffect} from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { fetchSalesReportSummary } from "../api/api";
// import { calculatedOrderProfit } from "./orders";
import { useNavigate } from "react-router-dom";
// import necessary components from react-chartjs-2
import { Line, Bar } from 'react-chartjs-2';
import { FaChartLine, FaChartBar } from "react-icons/fa";
import { AccountingScopeSelect, useAccountingScope } from "./Accounting/accountingscope";
// import the necessary components from Chart.js
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale } from 'chart.js';
// register the necessary chart.js components
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale);


function Reports() {
    const scope = useAccountingScope();
    const [salesReport, setSalesReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportError, setReportError] = useState('');
    const [ startDate, setStartDate ] = useState('');
    const [ endDate, setEndDate ] = useState('');
    const [graphType, setGraphType] = useState('line'); // or 'bar'

    const navigate = useNavigate();
    const selectedStore = scope.stores.find((store) => String(store.id) === String(scope.storeId))
        || (String(scope.currentStore?.id) === String(scope.storeId) ? scope.currentStore : null);
    const showCentralPurchaseMetrics = scope.storeId === "all" || selectedStore?.store_type === "WAREHOUSE";

    // useEffect(() => {
    //     fetchOrders();
    //     fetchAllPurchases();
    // }, [fetchOrders, fetchAllPurchases]);

    useEffect(() => {
        // if no chosen date range, default to current month/day, let's go with day for now
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
        setEndDate(today);
    }, []);

    useEffect(() => {
        if (startDate && endDate && !scope.loadingScope && scope.storeId) {
            let cancelled = false;
            setReportLoading(true);
            setReportError('');

            fetchSalesReportSummary({
                    start_date: startDate,
                    end_date: endDate,
                    store_id: scope.storeId,
                }).then((summary) => {
                if (!cancelled) setSalesReport(summary);
            }).catch((error) => {
                console.error('Failed to load report summary:', error);
                if (!cancelled) {
                    setSalesReport(null);
                    setReportError(error?.response?.data?.detail || 'Unable to generate the selected report.');
                }
            }).finally(() => {
                if (!cancelled) setReportLoading(false);
            });

            return () => {
                cancelled = true;
            };
        }
    }, [
        startDate,
        endDate,
        scope.loadingScope,
        scope.storeId,
    ]);

    const netPurchases = Number(salesReport?.net_purchases || 0);
    const numberOfPurchases = Number(salesReport?.purchase_count || 0);

    // Sales figures are aggregated by PostgreSQL and arrive as one small payload.
    const totalDiscounts = Number(salesReport?.discounts || 0);
    const grossSales = Number(salesReport?.gross_sales || 0);
    const totalReturns = Number(salesReport?.returns || 0);
    const totalSales = Number(salesReport?.total_sales || 0);
    const dailyAverageSales = Number(salesReport?.daily_average_sales || 0);
    const numberOfOrders = Number(salesReport?.order_count || 0);
    const valueOfProductsSold = Number(salesReport?.value_products_sold || 0);
    const totalProfit = Number(salesReport?.profit || 0);
    const profitPercentage = Number(salesReport?.profit_percentage || 0);
    const averageOrderValue = Number(salesReport?.average_order_value || 0);
    const topProduct = salesReport?.top_product || 'N/A';

    const totalVatPayable = Number(salesReport?.sales_vat || 0);
    const totalPurchaseVat = Number(salesReport?.purchase_vat || 0);
    const finalVatPayable = totalVatPayable - totalPurchaseVat;
    // end of added

    // added replacing 'prepare data for the month' above
    // for graphs
    // --- build an array of all dates in the chosen range ---
    const dateList = [];
    if (startDate && endDate) {
        for (let d = new Date(startDate);
             d <= new Date(endDate);
             d.setDate(d.getDate() + 1)) {
            dateList.push(new Date(d));
        }
    }

    // labels like 1 April, 2 April, etc.
    const labels = dateList.map(d =>
        d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    );

    const salesByDay = Object.fromEntries(
        (salesReport?.daily || []).map((row) => [row.day, Number(row.net_sales || 0)])
    );
    const ordersByDay = Object.fromEntries(
        (salesReport?.daily || []).map((row) => [row.day, Number(row.order_count || 0)])
    );

    // map totals back to the label order
    // both below are hash-maps, so we can use the dateList to get the totals for each day
    const salesData = dateList.map(d => salesByDay[d.toISOString().slice(0, 10)] || 0);
    const ordersData = dateList.map(d => ordersByDay[d.toISOString().slice(0, 10)] || 0);
    // end of added

    return (
        <>
            <h1>Reports</h1>
            {reportError && <Alert variant="danger">{reportError}</Alert>}
            {reportLoading && (
                <Alert variant="info" className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> Generating report…
                </Alert>
            )}
            <Form className="mb-4">
                <Row>
                    {scope.isAdmin && (
                        <Col md={4}>
                            <AccountingScopeSelect scope={scope} label="Report Scope"/>
                        </Col>
                    )}
                    <Col md={3}>
                        <Form.Group controlId="startDate">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="endDate">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Form>

            <Row className="mb-4">
            {/* first row */}
                <Col md={3}>
                    <Card className="text-center bg-info">
                        <Card.Body>
                            <Card.Title>Total Sales</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalSales.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                            })}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center bg-secondary">
                        <Card.Body>
                            <Card.Title>Gross Sales</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{grossSales.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center bg-danger" style={{ cursor: 'pointer' }} onClick={() => navigate("/orders/returns")}>
                        <Card.Body>
                            <Card.Title>Order Returns</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalReturns.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center bg-warning" style={{ cursor: 'pointer' }} onClick={() => navigate("/orders")}>
                        <Card.Body>
                            <Card.Title>Number of Orders</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{numberOfOrders}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>

                {/*second row*/}
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-success" style={{ cursor: 'pointer' }} onClick={() => navigate("/reports/products")}>
                        <Card.Body>
                            <Card.Title>Value of Products Sold</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{valueOfProductsSold.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-body-tertiary">
                        <Card.Body>
                            <Card.Title>Total Profit</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalProfit.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-primary">
                        <Card.Body>
                            <Card.Title>Profit Percentage</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                                {profitPercentage.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-info">
                        <Card.Body>
                            <Card.Title>Average Order Value</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{averageOrderValue.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                            })}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-light">
                        <Card.Body>
                            <Card.Title>Daily Average Sales</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                                {dailyAverageSales.toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-secondary">
                        <Card.Body>
                            <Card.Title>Top Selling Product</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold' }}>{topProduct}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="mt-4">
                    <Card className="text-center bg-danger">
                        <Card.Body>
                            <Card.Title>Total Discounts</Card.Title>
                            <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalDiscounts.toLocaleString()}</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
                {showCentralPurchaseMetrics && (
                    <>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-warning">
                                <Card.Body>
                                    <Card.Title>Net Purchases</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{netPurchases.toLocaleString()}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-success" style={{ cursor: 'pointer' }} onClick={() => navigate("/inventory/purchases")}>
                                <Card.Body>
                                    <Card.Title>Number of Purchases</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{numberOfPurchases}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-info">
                                <Card.Body>
                                    <Card.Title>Sales VAT</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalVatPayable.toLocaleString()}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-secondary">
                                <Card.Body>
                                    <Card.Title>Purchase VAT</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>{totalPurchaseVat.toLocaleString()}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-4">
                            <Card className="text-center bg-danger">
                                <Card.Body>
                                    <Card.Title>VAT Payable</Card.Title>
                                    <Card.Text style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
                                        {finalVatPayable.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>

            {/*icons to switch graph types*/}
            <div className="d-flex justify-content-end mb-3">
                <Button variant={graphType === 'line' ? 'primary' : 'secondary'}
                        onClick={() => setGraphType('line')} className="button-spacing">
                    <FaChartLine size={20} />
                </Button>
                <Button variant={graphType === 'bar' ? 'primary' : 'secondary'}
                        onClick={() => setGraphType('bar')}>
                    <FaChartBar size={20} />
                </Button>

                {/*{graphType === 'line' ? (*/}
                {/*    <Line key="line-chart" data={chartData} />*/}
                {/*) : (*/}
                {/*    <Bar key="bar-chart" data={chartData} />*/}
                {/*)}*/}
            </div>

            {/* graph */}
            <Row>
                <Col md={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Monthly Net Sales</Card.Title>
                            {graphType === 'line' ? (
                                <Line
                                    key="line-chart"  // add a unique key to the chart to force a re-render of chart type
                                    data={{
                                        labels,
                                        datasets: [{
                                            label: 'Net Sales',
                                            data: salesData,
                                            backgroundColor: 'rgba(75, 192, 192, 0.4)',
                                            borderColor: 'rgba(75, 192, 192, 1)',
                                            fill: false,
                                        }]
                                    }}
                                />
                            ) : (
                                <Bar
                                    key="bar-chart"  // add a unique key to the chart to force a re-render of chart type
                                    data={{
                                        labels,
                                        datasets: [{
                                            label: 'Net Sales',
                                            data: salesData,
                                            backgroundColor: 'rgba(75, 192, 192, 0.4)',
                                        }]
                                    }}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Monthly Orders</Card.Title>
                            {graphType === 'line' ? (
                                <Line data={{
                                    labels,
                                    datasets: [{
                                        label: 'Number of Orders',
                                        data: ordersData,
                                        backgroundColor: 'rgba(153, 102, 255, 0.4)',
                                        borderColor: 'rgba(153, 102, 255, 1)',
                                        fill: false,
                                    }]
                                }} />
                            ) : (
                                <Bar data={{
                                    labels,
                                    datasets: [{
                                        label: 'Orders',
                                        data: ordersData,
                                        backgroundColor: 'rgba(153, 102, 255, 0.4)',
                                    }]
                                }} />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

        </>
    )
}

export default Reports;